// RecommendationsTable.js
// Stock recommendations table with per-row checkbox selection.
// Unchecking a trade removes it from all portfolio KPI calculations.
import React from 'react';

const fmtNum = (v) =>
    v != null
        ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })
        : '—';

const statusBadgeCls = (t) => {
    if (t.status === 'active') return 'active';
    return t.pct >= 0 ? 'closed-win' : 'closed-loss';
};

const TABS = ['all', 'active', 'closed'];

const RecommendationsTable = ({
    trades,
    selected,
    currentTab,
    onTabChange,
    onToggle,
    onToggleAll,
}) => {
    const filtered = trades.filter((t) => {
        if (currentTab === 'active') return t.status === 'active';
        if (currentTab === 'closed') return t.status === 'closed';
        return true;
    });

    // Header checkbox state
    const allFilteredSelected = filtered.length > 0 && filtered.every((t) => selected.includes(t.id));
    const someFilteredSelected = filtered.some((t) => selected.includes(t.id));

    return (
        <div className="recommendations-card">
            {/* Table header row */}
            <div className="rec-table-header">
                <span className="rec-table-title">Stock Recommendation Report</span>
                <div className="port-tab-bar">
                    {TABS.map((tab) => (
                        <button
                            key={tab}
                            className={`port-tab-btn ${currentTab === tab ? 'active' : ''}`}
                            onClick={() => onTabChange(tab)}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="rec-table-wrap">
                <table className="rec-table">
                    <caption className="visually-hidden">Stock recommendations</caption>
                    <thead>
                        <tr>
                            <th style={{ width: 28 }} scope="col">
                                <input
                                    type="checkbox"
                                    className="port-checkbox"
                                    checked={allFilteredSelected}
                                    ref={(el) => {
                                        if (el)
                                            el.indeterminate =
                                                someFilteredSelected && !allFilteredSelected;
                                    }}
                                    onChange={onToggleAll}
                                    aria-label="Select or deselect all visible recommendations"
                                    title="Select / deselect all visible"
                                />
                            </th>
                            <th scope="col">Stock</th>
                            <th scope="col">Date</th>
                            <th scope="col">Reco</th>
                            <th scope="col">Range</th>
                            <th scope="col">Trigger</th>
                            <th scope="col">CMP</th>
                            <th scope="col">Return %</th>
                            <th scope="col">Target</th>
                            <th scope="col">Stop Loss</th>
                            <th scope="col">Upside %</th>
                            <th scope="col">Risk %</th>
                            <th scope="col">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((t) => {
                            const isSel  = selected.includes(t.id);
                            const pctCls = t.pct >= 0 ? 'pos' : 'neg';

                            return (
                                <tr key={t.id} className={!isSel ? 'deselected' : ''}>
                                    {/* Checkbox */}
                                    <td>
                                        <input
                                            type="checkbox"
                                            className="port-checkbox"
                                            checked={isSel}
                                            onChange={() => onToggle(t.id)}
                                        />
                                    </td>

                                    {/* Stock name + sector */}
                                    <td>
                                        <div className="rec-stock-name">{t.stock}</div>
                                        <div className="rec-stock-sub">{t.sector}</div>
                                    </td>

                                    <td style={{ color: '#5b6573', whiteSpace: 'nowrap' }}>{t.date}</td>

                                    <td style={{ color: '#15803d', fontWeight: 700, fontSize: '0.78rem' }}>
                                        {t.action}
                                    </td>

                                    <td style={{ whiteSpace: 'nowrap' }}>{t.range}</td>

                                    <td>₹{fmtNum(t.trigger)}</td>

                                    <td>{t.ltp != null ? `₹${fmtNum(t.ltp)}` : '—'}</td>

                                    {/* % change */}
                                    <td>
                                        <span className={`port-pct ${pctCls}`}>
                                            {t.pct >= 0 ? '+' : ''}{t.pct.toFixed(2)}%
                                        </span>
                                    </td>

                                    <td>₹{fmtNum(t.target)}</td>
                                    <td>₹{fmtNum(t.sl)}</td>

                                    {/* Potential reward — green sparkbar */}
                                    <td>
                                        <div style={{ fontSize: '0.76rem', color: '#15803d', fontWeight: 700 }}>
                                            {t.reward}%
                                        </div>
                                        <div
                                            className="mini-bar green"
                                            style={{ width: `${Math.min(t.reward, 100)}%` }}
                                        />
                                    </td>

                                    {/* Potential risk — red sparkbar */}
                                    <td>
                                        <div style={{ fontSize: '0.76rem', color: '#b91c1c', fontWeight: 700 }}>
                                            {t.risk}%
                                        </div>
                                        <div
                                            className="mini-bar red"
                                            style={{ width: `${Math.min(t.risk * 3, 100)}%` }}
                                        />
                                    </td>

                                    {/* Status badge */}
                                    <td>
                                        <span className={`status-badge ${statusBadgeCls(t)}`}>
                                            {t.status.toUpperCase()}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}

                        {filtered.length === 0 && (
                            <tr>
                                <td
                                    colSpan={13}
                                    style={{
                                        textAlign: 'center',
                                        padding: '2rem',
                                        color: '#5b6573',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    No {currentTab !== 'all' ? currentTab : ''} recommendations found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default RecommendationsTable;
